import { buildContextFromProfile } from "../baselines/records";
import {
  buildEffectiveOrgProfileData,
  type EffectiveOrgProfileInput,
} from "./effective-org-profile";

/** Stored-org-profile shape `resolveModelContext` resolves a context from. */
export type ModelContextProfileInput = EffectiveOrgProfileInput & {
  bedCount?: number | null;
  annualRevenue?: number | bigint | null;
  physicianCount?: number | null;
};

export interface ModelContextWithOrgContext {
  resolvedContext: Record<string, number>;
  orgContext: Record<string, number>;
  overrideContext: Record<string, number>;
}

/**
 * Resolve both the effective model context and the org-owned portion used to
 * derive it. Building both views together keeps source attribution from
 * repeating the effective-profile/context computation.
 */
export function resolveModelContextWithOrgContext(
  definition: {
    contextValues?: Record<string, number>;
    contextOverrides?: Record<string, number>;
  },
  profile: ModelContextProfileInput | null | undefined,
): ModelContextWithOrgContext {
  const snapshot = definition.contextValues ?? {};
  const storedOverrides = definition.contextOverrides ?? {};
  if (!profile) {
    return {
      resolvedContext: { ...snapshot },
      orgContext: {},
      overrideContext: {},
    };
  }
  const { profileData, baselineRecords } =
    buildEffectiveOrgProfileData(profile);
  const orgContext = buildContextFromProfile({
    profileData,
    baselineRecords,
    bedCount: profile.bedCount,
    annualRevenue: profile.annualRevenue,
    physicianCount: profile.physicianCount,
  });
  const overrideContext: Record<string, number> = {};
  for (const [key, value] of Object.entries(storedOverrides)) {
    if (Object.hasOwn(orgContext, key)) overrideContext[key] = value;
  }
  const resolvedContext: Record<string, number> = { ...orgContext };
  for (const [key, value] of Object.entries(snapshot)) {
    if (!Object.hasOwn(orgContext, key)) resolvedContext[key] = value;
  }
  for (const [key, value] of Object.entries(overrideContext)) {
    resolvedContext[key] = value;
  }
  return { resolvedContext, orgContext, overrideContext };
}

/**
 * Resolve the context a model should be computed against. Precedence is:
 * explicit opportunity-local overrides, then fresh org-derived context, then
 * the persisted generation snapshot for keys the organization does not own.
 *
 * The org-derived context is built from the EFFECTIVE profile — facts folded
 * into `profileData`/`baselineRecords` via `buildEffectiveOrgProfileData` — so
 * it matches exactly what model generation computes against (generation runs
 * the same effective merge before `buildContextFromProfile`). Without this the
 * raw stored columns would be used, silently dropping fact-only values and any
 * fact that supersedes a stale column value.
 *
 * Org-governed keys are refreshed on every recompute, so the snapshot's copy of
 * any key the profile produces is dropped. Snapshot-only keys survive as
 * fallback context, while active `definition.contextOverrides` for org-owned
 * keys win. Used identically by the server recompute and the frontend live ROI
 * so the saved projection matches what the user sees. Without a profile, no
 * stored overrides are active and the resolver returns the snapshot.
 */
export function resolveModelContext(
  definition: {
    contextValues?: Record<string, number>;
    contextOverrides?: Record<string, number>;
  },
  profile: ModelContextProfileInput | null | undefined,
): Record<string, number> {
  return resolveModelContextWithOrgContext(definition, profile).resolvedContext;
}
